import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import {
  getActivationExperienceDescription,
  getActivationExperienceLabel,
  getActivationStatusStyle,
  type ActivationExperienceValue,
  type ActivationStatusValue,
} from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"

export type TenantActivationStatusBadgeProps = ComponentProps<"span"> & {
  status: ActivationStatusValue
  /**
   * The onboarding framing the activation delivers(ed) — rendered as a
   * muted tag after the status so CS sees WHICH journey each user got
   * (FUS-927). Omitted/null (no activation) renders no tag.
   */
  experience?: ActivationExperienceValue | null
  /**
   * Drops the native title attributes — set when the badge sits inside a
   * rich tooltip trigger (UserMigrationCell) so the browser tooltip can't
   * double up on it.
   */
  suppressTitle?: boolean
}

function TenantActivationStatusBadge(props: TenantActivationStatusBadgeProps) {
  const { status, experience, suppressTitle, className, ...spanProps } = props
  const style = getActivationStatusStyle(status)
  const experienceLabel = getActivationExperienceLabel(experience)

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-xs text-muted-foreground",
        className,
      )}
      title={suppressTitle ? undefined : style.description}
      {...spanProps}
    >
      <Icon
        className={`shrink-0 ${style.iconColor}`}
        src={style.iconSrc}
        size={14}
      />
      {style.label}
      {experienceLabel ? (
        <span
          className="rounded border border-border px-1 py-px text-[10px] leading-tight text-muted-foreground/80"
          title={
            suppressTitle
              ? undefined
              : getActivationExperienceDescription(experience)
          }
        >
          {experienceLabel}
        </span>
      ) : null}
    </span>
  )
}

export default TenantActivationStatusBadge
